"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/lib/auth-store"
import { useRouter, useSearchParams } from "next/navigation"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { hasActiveSubscriptionStatus } from "@/lib/entitlements"
import { useTranslations } from "next-intl"
import { useModalContent } from "@/lib/modal-content"
import { usePatreonMode } from "@/lib/use-patreon-mode"

const DISMISSED_KEY = "modal:founder_plus_pitch"
const DELAY_MS = 1200

// Same set used by app/api/badge-frames/route.ts, lib/discord/client.ts, and
// lib/badges/compute.ts to decide real Founder+ eligibility — a Founder
// actively carrying any of these tiers on top already IS Founder+ (badge,
// frame, Discord role all already granted) and should never see this pitch.
// Deliberately excludes "founder" itself — that's the baseline, not the
// extra support on top of it.
const FOUNDER_PLUS_TIERS = new Set([
  "supporter", "goon", "goon_plus", "goon_max",
  "goontier_1", "goontier_2", "goontier_basic", "goontier_pro",
])

/** Pitches existing Founders on Founder+ (stack any paid tier on top of the
 * one-time Founder purchase to get a badge/frame/Discord role — see
 * lib/badges/compute.ts isFounderPlus for the real mechanic, already live).
 * Deliberately NOT the same-session Stripe success modal (app/subscribe/
 * page.tsx) — this is for a Founder's later return visit, once, worded to
 * reassure their $79 purchase is untouched before asking anything (see
 * session discussion: the real risk here is reading as a bait-and-switch
 * against "no recurring charge, ever"). */
export function FounderPlusModal({ forceOpen = false, onClose }: { forceOpen?: boolean; onClose?: () => void } = {}) {
  const t = useTranslations("founderPlusModal")
  const [visible, setVisible] = useState(false)
  const content = useModalContent("founder_plus_pitch", visible)
  const { user, profile, isLoading: authLoading } = useAuthStore()
  const router = useRouter()
  const { enabled: patreonMode } = usePatreonMode()
  const searchParams = useSearchParams()
  // Dev-only bypass: visit ?preview=founder-plus locally to see this
  // instantly, skipping eligibility, the delay, and the seen-state gate.
  const forcePreview = process.env.NODE_ENV !== "production" && searchParams.get("preview") === "founder-plus"

  const isFounder = profile?.is_founder === true
  const isAlreadyFounderPlus = isFounder
    && FOUNDER_PLUS_TIERS.has(profile?.subscription_tier ?? "")
    && hasActiveSubscriptionStatus(profile?.subscription_status)

  useEffect(() => {
    if (forceOpen || forcePreview) { setVisible(true); return }
    // Patreon mode: a Patreon Founder becomes Founder+ by staying 2 paid months, so there is nothing to pitch.
    if (patreonMode) return
    if (authLoading || !user || !profile) return
    if (!isFounder || isAlreadyFounderPlus) return

    let cancelled = false
    const run = async () => {
      const { keys } = await fetchSeenState()
      if (cancelled || keys.has(DISMISSED_KEY)) return
      setTimeout(() => { if (!cancelled) setVisible(true) }, DELAY_MS)
    }
    void run()
    return () => { cancelled = true }
  }, [forceOpen, forcePreview, patreonMode, authLoading, user, profile, isFounder, isAlreadyFounderPlus])

  const dismiss = () => {
    if (forceOpen) { onClose?.(); return }
    setVisible(false)
    void markSeen(DISMISSED_KEY)
  }

  const upgrade = () => {
    dismiss()
    // /subscribe already detects is_founder and reframes itself ("Want to
    // support Goonru even further?") above the same tier cards — no
    // separate Founder+ page needed.
    router.push("/subscribe")
  }

  const canShow = useModalSlot("founderPlus", visible && content.ready, { bypass: forceOpen || forcePreview })

  if (!visible || !content.ready || !canShow) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={dismiss} />

      {/* goopborder-slime-portrait.webp (941×1672, ratio 0.5628 ≈ 9:16) — same
          fixed-aspect + object-cover + unclipped overscanning border pattern
          as components/modals/glymtober-card.tsx, so this modal carries the
          same frame as the rest of the family instead of its own plain
          border. */}
      {/* The card is shorter than 9:16 (440×CARD_H) — the art "stage" below
          keeps its full 9:16 size anchored to the top, so the character stays
          the same scale and the extra empty bottom is simply cropped off. The
          portrait border stretches ~12% vertically to fit; barely visible. */}
      <div
        className="relative z-10 w-full max-w-[440px] aspect-[440/712]"
        style={{ zoom: "min(1, calc(95vw / 440px), calc(95svh / 712px))" }}
      >
        <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl">
         <div className="absolute top-0 left-0 w-full aspect-[9/16]">
          {/* Single combined piece — character is already painted onto the
              background art (see lib/modal-content-defs.ts), unlike the other
              modals in this family that layer a separate character image on
              top of a generic background. object-cover crops a little off
              each side to fill the taller 9:16 frame; nothing vertical is
              lost since the art is relatively wider than 9:16, not taller. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={content.art("background")}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />

          {/* The Founder+ badge itself, sitting in the king's outstretched
              hand. Hardcoded (not admin content) — its position is tuned to
              this exact character pose, not swappable independently of it. */}
          {/* The gif's 457×457 canvas is ~37% transparent margin; its real
              opaque bounds across all 48 frames (measured with sharp) are
              x 84–372, y 89–368 (289×280). This box is exactly that region,
              so `width` sizes the visible badge, not its empty padding. */}
          <div
            className="absolute z-[5] overflow-hidden pointer-events-none"
            style={{ left: "68%", top: "42%", width: "23%", aspectRatio: "289 / 280" }}
          >
            <div className="absolute" style={{ left: "-29.07%", top: "-31.79%", width: "158.13%", aspectRatio: "1" }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/badges/founderplus_badgefloat.gif"
                alt=""
                className="absolute inset-0 w-full h-full"
                draggable={false}
              />

              {/* Shine sweep masked by the gif's own alpha, so it only lights
                  the opaque badge (coin + wings) and follows its float. */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  WebkitMaskImage: "url(/badges/founderplus_badgefloat.gif)",
                  maskImage: "url(/badges/founderplus_badgefloat.gif)",
                  WebkitMaskSize: "100% 100%",
                  maskSize: "100% 100%",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                }}
              >
                <div
                  className="absolute inset-y-0 left-0 w-[45%] animate-badge-shine-sweep"
                  style={{ background: "linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)" }}
                />
              </div>
            </div>
          </div>
         </div>

          {/* Shadow rising up behind the text/button — same "fades up behind
              text" gradient glymtober-card.tsx and energy-limit-modal.tsx use,
              not just relying on the art's own natural darkness. */}
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              top: "62%",
              background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.9) 55%, rgba(0,0,0,0.95) 100%)",
            }}
          />

          {/* Content overlays the art's own dark lower band — this copy is a
              real paragraph (reassurance + the pitch), not a short bullet
              list, so it needs its own real space instead of fighting the
              art for room in a dark corner. */}
          <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-3 flex flex-col gap-2">
            <div className="text-center space-y-1.5">
              <p className="font-bold text-xl text-white drop-shadow-sm">
                {content.text("title", t("thankYouFounder"))}
              </p>
              <p className="text-[14px] text-white/70 leading-snug">
                {content.text("body", t("yourSpotIsLockedInForLifeThatNeverChang"))}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                size="lg"
                className="w-full font-semibold text-base bg-amber-500 hover:bg-amber-400 text-black"
                onClick={upgrade}
              >
                {content.text("cta", t("seeFounderPlusTiers"))}
              </Button>
              <Button
                variant="ghost"
                onClick={dismiss}
                className="w-full rounded-full text-xs font-medium text-white/60 hover:text-white/90 hover:bg-white/5"
              >
                {content.text("dismiss", t("noThankYou"))}
              </Button>
            </div>
          </div>
        </div>

        {/* Goop border — unclipped, overscanning slightly past the card's own
            edges (same -3%/-3% convention glymtober-card.tsx and
            app/too-young/page.tsx use for this same asset) so the frame reads
            as wrapping AROUND the card rather than sitting inset inside it. */}
        <div className="absolute z-20 pointer-events-none" style={{ inset: "-3% -3%" }}>
          <Image src="/borders/goopborder-slime-portrait.webp" alt="" fill style={{ objectFit: "fill" }} unoptimized />
        </div>
      </div>
    </div>
  )
}
